/**
 * Rules 2-bis and 2-quinquies — recognising the garment by its name.
 *
 * Both used to rest on a code the operator could read. There is none. Our SKU is
 * composed here in the orchestrator (`«product code»-«colour id»`); DecoNetwork
 * has no field to read it from — `vendor_sku` is empty on the real PO 552283,
 * `sku` and `gtin` are inconsistent or absent; and no garment tag carries it —
 * Payper prints its own EAN-13, Beechfield, B&C and Premier a style code and a
 * size. The one label bearing our SKU is the crate label, which rule 3 prints
 * *after* counting, hence after the hook: for the code you need the label, for
 * the label you need the hook, for the hook you need the code.
 *
 * So on 2026-08-26 the code was abandoned and the name became the road, not the
 * exception (`docs/superpowers/specs/2026-08-26-il-nome-al-posto-del-codice-design.md`).
 * One verb answers both rules — «which expected lines look like what was typed»
 * — and one screen draws it, in the two places the bench needs it: the gesture
 * zone when nothing is hooked, the panel when something is.
 *
 * It lives in the domain and not in the React component because it is a numbered
 * rule, and a numbered rule that lives in a component stops being checkable
 * against the prototype.
 */
/**
 * Lowercase, trimmed, inner runs of whitespace collapsed to one space.
 *
 * Query and haystack go through the same funnel, which is the point: two
 * normalisations that drift apart make a search that fails on a space.
 */
function normalize(text) {
    return text.trim().toLowerCase().replace(/\s+/g, ' ');
}
/**
 * Everything about a line the operator could plausibly type, as one string.
 *
 * The SKU is in here too, and it is the whole of what survives of the code: type
 * `PL330-BLR` and it matches like any other word. One case of one search, not a
 * second road to keep alive.
 */
function haystack(line) {
    return normalize(`${line.articolo} ${line.colore} ${line.cliente} ${line.fornitore} ${line.sku}`);
}
/**
 * The expected lines matching what the operator typed.
 *
 * **Word by word, not one substring.** On the tag the article and the colour sit
 * on separate lines — `art: ROYAL`, `color: IRON GREY` — and the operator types
 * them in a row. Matching the whole query as a single substring would fail on
 * exactly that, the most ordinary case there is; requiring every word to appear
 * somewhere lets `royal iron grey` find the line, in any order.
 *
 * **The incoming order is kept, and that matters.** The caller passes the lines
 * oldest purchase order first, and the screen tells the operator to take the
 * first one. Re-ranking by relevance here would quietly undo rule 2-bis — the
 * whole reason the system, and not the operator, decides who gets served first.
 *
 * Lines already fully received are dropped: that is rule 7 read at line level,
 * the same reading `resolveHook` does size by size. Hooking one would lead
 * nowhere, and offering it invites the operator to try.
 *
 * An empty query returns nothing rather than everything: a list of every line in
 * the window is not an answer to a question nobody asked yet.
 */
export function searchLines(lines, query) {
    const words = normalize(query).split(' ').filter(Boolean);
    if (words.length === 0)
        return [];
    return lines.filter((line) => {
        if (line.stato === 'ok')
            return false;
        const testo = haystack(line);
        return words.every((word) => testo.includes(word));
    });
}
//# sourceMappingURL=search.js.map